"use client";

import { useCallback } from "react";
import type { FilterColumnDef } from "./types";
import { formatNumberForDisplay } from "./helpers";
import { Select } from "@/components/forms";

interface FilterValueInputProps {
  columnDef: FilterColumnDef;
  value: string;
  onChange: (value: string) => void;
  /** For multi-select: the currently selected values. */
  selectedValues?: string[];
  /** For multi-select: called when selection changes. */
  onSelectedValuesChange?: (values: string[]) => void;
  onSubmit?: () => void;
  /** Compact mode for inline filter rows. */
  compact?: boolean;
  /** Whether the value is empty (shows amber border highlight). */
  isEmpty?: boolean;
}

const baseInputClasses =
  "border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navigator-primary focus:border-transparent";

function getInputClasses(compact?: boolean, isEmpty?: boolean) {
  const size = compact ? "px-2.5 py-1.5" : "px-3 py-2";
  const width = "w-full";
  const border = isEmpty ? "border-amber-400" : "border-gray-200";
  return `${width} ${size} ${baseInputClasses.replace("border-gray-200", border)}`;
}

export function FilterValueInput({
  columnDef,
  value,
  onChange,
  selectedValues = [],
  onSelectedValuesChange,
  onSubmit,
  compact,
  isEmpty,
}: FilterValueInputProps) {
  const cls = getInputClasses(compact, isEmpty);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSubmit?.();
    },
    [onSubmit]
  );

  if (columnDef.type === "date") {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={cls}
      />
    );
  }

  if (columnDef.type === "number") {
    return (
      <input
        type="text"
        value={formatNumberForDisplay(value)}
        onChange={(e) => {
          const sanitized = e.target.value
            .replace(/,/g, "")
            .replace(/[^0-9.-]/g, "");
          const parts = sanitized.split(".");
          const normalized =
            parts.length > 2
              ? `${parts[0]}.${parts.slice(1).join("")}`
              : sanitized;
          onChange(normalized);
        }}
        onKeyDown={(e) => {
          const allowed = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
            "Home",
            "End",
            "Enter",
          ];
          if (
            e.ctrlKey ||
            e.metaKey ||
            allowed.includes(e.key) ||
            /[0-9]/.test(e.key) ||
            (e.key === "." && !value.includes(".")) ||
            (e.key === "-" &&
              (e.currentTarget as HTMLInputElement).selectionStart === 0 &&
              !value.startsWith("-"))
          ) {
            if (e.key === "Enter") onSubmit?.();
            return;
          }
          e.preventDefault();
        }}
        placeholder="Enter number"
        className={cls}
      />
    );
  }

  if (columnDef.type === "select" && columnDef.options) {
    return (
      <div className="w-full">
        <Select
          selectSize={compact ? "xs" : "default"}
          value={value}
          onChange={(v) => onChange(v as string)}
          placeholder="Select value"
          options={columnDef.options.map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          className={isEmpty ? "border-amber-400" : ""}
        />
      </div>
    );
  }

  if (columnDef.type === "multi-select" && columnDef.options) {
    return (
      <div className="w-full">
        <Select
          mode="multiple"
          selectSize={compact ? "xs" : "default"}
          value={selectedValues}
          onChange={(v) => onSelectedValuesChange?.(v as string[])}
          placeholder="Select values"
          options={columnDef.options}
          searchable={columnDef.options.length > 8}
          showSelectAll
          selectAllLabel="Select All"
          className={isEmpty ? "border-amber-400" : ""}
        />
      </div>
    );
  }

  // Default: text input
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder="Enter filter value"
      className={cls}
    />
  );
}
